import './JiraIntegration.scss';

import { AsyncStateNotification } from '@ui/notifications';
import { Form } from 'antd';
import { integrationsActions } from '@sonar-software/sonar-integrations';
import { JiraCredentialsEditButton } from './JiraCredentialsEditButton';
import { JiraCredentialsForm } from './JiraCredentialsForm';
import { JiraIcon } from '@ui/icons';
import { JiraProjectSelect } from './JiraProjectSelect';
import { JiraSelectedProjects } from './JiraSelectedProjects';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import React from 'react';
import { SettingsSection } from '../SettingsSection';
import { SonarCard } from '@ui/general';

import { COPY_SPECIFIC, INTEGRATIONS_COPY, NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import { CopyText, SonarText } from '@ui/text';
import { Else, If, Then, When } from 'react-if';
import { jiraIntegrationSelectors, jiraIntegrationUpdateSelectors, jiraSummarySelectors } from './@arch/selectors';
import { useAction, useCurrentUser, useOnMount, useSonarSelector } from '@hooks';

const cardTitle =
	<div className='jira-integration-card-title'>
		<JiraIcon className='jira-icon' />
		<div className='h1'>{ COPY_SPECIFIC.JIRA_INTEGRATION }</div>
	</div>;

export const JiraIntegration: React.FC = () => {
	const [ form ] = Form.useForm();
	const { isReadOnly } = useCurrentUser();

	const getJiraSummary = useAction(integrationsActions.requestGetJiraSummary);
	const jiraSummary = useSonarSelector(jiraSummarySelectors.data);
	const isSummaryRequested = useSonarSelector(jiraSummarySelectors.isRequested);
	const isSingleProject = Number(jiraSummary?.selectedProjects.length) === 1 && !jiraSummary?.selectedProjectKey;

	useOnMount(() => {
		getJiraSummary();
	});

	return (
		<>
			<SonarCard
				extra={ jiraSummary && !isReadOnly ? <JiraCredentialsEditButton form={ form } /> : undefined }
				id='jira-integration'
				title={ cardTitle }>
				<div className='jira-container'>
					<LoadingSpinner spinning={ isSummaryRequested }>
						<If condition={ !jiraSummary?.connected }>
							<Then>
								<SonarText className='integration-copy'>{ INTEGRATIONS_COPY.CONNECT_JIRA }</SonarText>
								<JiraCredentialsForm form={ form } />
							</Then>
							<Else>
								<SettingsSection title={ INTEGRATIONS_COPY.INSTANCE_URL }>
									<CopyText text={ jiraSummary?.url } />
								</SettingsSection>
								<SettingsSection title={ INTEGRATIONS_COPY.WEBHOOK_URL }>
									<CopyText text={ jiraSummary?.webhookUrl } />
								</SettingsSection>
								<When condition={ !isSingleProject }>
									<SettingsSection
										subheading={ INTEGRATIONS_COPY.CHOOSE_PROJECT }
										title={ INTEGRATIONS_COPY.JIRA_PROJECT_FILTER }>
										<JiraProjectSelect />
									</SettingsSection>
								</When>
								<SettingsSection
									subheading={ INTEGRATIONS_COPY.MAP_JIRA }
									title={ INTEGRATIONS_COPY.JIRA_STATUS }>
									<JiraSelectedProjects />
								</SettingsSection>
							</Else>
						</If>
					</LoadingSpinner>
				</div>
			</SonarCard>
			<AsyncStateNotification
				errored={ NOTIFICATIONS_COPY.ERROR_POST_JIRA_INTEGRATION }
				failed={ NOTIFICATIONS_COPY.FAILED_POST_JIRA_INTEGRATION }
				received={ {
					description: NOTIFICATIONS_COPY.SUCCESS_POST_JIRA_INTEGRATION_DESCRIPTION,
					options: { message: NOTIFICATIONS_COPY.SUCCESS_POST_JIRA_INTEGRATION_MESSAGE }
				  } }
				resetRawAction={ integrationsActions.resetPostJiraCredentials }
				stateSelector={ jiraIntegrationSelectors.state } />
			<AsyncStateNotification
				errored={ NOTIFICATIONS_COPY.ERROR_PUT_JIRA_INTEGRATION }
				failed={ NOTIFICATIONS_COPY.FAILED_PUT_JIRA_INTEGRATION }
				received={ {
					description: NOTIFICATIONS_COPY.SUCCESS_PUT_JIRA_INTEGRATION_DESCRIPTION,
					options: { message: NOTIFICATIONS_COPY.SUCCESS_PUT_JIRA_INTEGRATION_MESSAGE }
				  } }
				resetRawAction={ integrationsActions.resetPutJiraCredentials }
				stateSelector={ jiraIntegrationUpdateSelectors.state } />
		</>
	);
};
